
import React, { useEffect } from 'react';
import { SuccessStoryData, successStories } from '@/data/successStories';
import { ArrowLeft, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SuccessStoryBentoCard from '../ui/SuccessStoryBentoCard';
import { featuredAchievers } from '@/data/articles';

interface SuccessStoryDetailProps {
  story: SuccessStoryData;
}

// Define an interface for the formatted content structure
interface FormattedContent {
  introduction: string;
  sections: Array<{
    heading: string;
    content: string[];
  }>;
}

const SuccessStoryDetail: React.FC<SuccessStoryDetailProps> = ({ story }) => {
  const navigate = useNavigate();

  // Add useEffect to scroll to top when component mounts or story changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [story.id]); // Add story.id as dependency to trigger when story changes

  // If there's no full story data, show a placeholder
  if (!story.fullStory) {
    return (
      <div className="py-12 px-4 md:px-6 text-center">
        <div className="container mx-auto max-w-3xl">
          <p className="text-gray-600 mb-6">Full story details are not available for this success story yet.</p>
          <Button variant="outline" asChild>
            <Link to="/success-story" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              <span>Back to Success Stories</span>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Get the achiever details if achieverId is present
  const achiever = story.achieverId
    ? featuredAchievers.find(a => a.id === story.achieverId)
    : null;

  // Format story content with headings and bullet points
  const formatStoryContent = (): FormattedContent => {
    const storyContent = story.fullStory.story;

    if (!storyContent || storyContent.length === 0) {
      return { introduction: '', sections: [] };
    }

    // First paragraph as introduction
    const introduction = storyContent[0];

    // Format remaining paragraphs as sections with headings and bullet points
    const sections = [];

    // Create structured content sections
    if (storyContent.length > 1) {
      // Add journey section
      sections.push({
        heading: "The Journey",
        content: storyContent.slice(1, 3)
      });

      // Add challenges and strategies section if available
      if (storyContent.length > 3) {
        sections.push({
          heading: "Challenges & Strategies",
          content: storyContent.slice(3, 5)
        });
      }

      // Add impact and lessons section if available
      if (storyContent.length > 5) {
        sections.push({
          heading: "Impact & Lessons",
          content: storyContent.slice(5)
        });
      }
    }

    return { introduction, sections };
  };

  // Get related stories (excluding current story)
  const getRelatedStories = () => {
    // Simple algorithm to find related stories based on stats categories or hashtags
    const relatedByCategory = successStories.filter(s =>
      s.id !== story.id && (
        (story.stats.revenue && s.stats.revenue) ||
        (story.stats.impact && s.stats.impact) ||
        (story.stats.employeeCount && s.stats.employeeCount) ||
        (story.stats.partners && s.stats.partners) ||
        (story.fullStory?.hashtags?.some(tag =>
          s.fullStory?.hashtags?.includes(tag)
        ))
      )
    );

    // Return top 3 related stories
    return relatedByCategory.slice(0, 3);
  };

  const formattedContent = formatStoryContent();
  const relatedStories = getRelatedStories();

  // Add function to handle related story click
  const handleRelatedStoryClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="py-8 md:py-10 px-4 md:px-6 mx-auto w-[95%] md:w-[80%] max-w-6xl">
      {/* Back button */}
      <Button variant="ghost" asChild className="mb-6 hover:bg-purple-50">
        <Link to="/success-story" className="flex items-center gap-2 text-purple-700">
          <ArrowLeft size={16} />
          <span>Back to Success Stories</span>
        </Link>
      </Button>

      {/* Hero image and title */}
      <div className="w-full h-[400px] md:h-[500px] overflow-hidden relative rounded-xl mb-8 shadow-md">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-transparent to-black/70 z-10"></div>
        <img
          src={story.thumbnail || story.image}
          alt={story.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white z-20">
          <h1 className="font-bold text-2xl md:text-4xl leading-tight">
            {story.fullStory.title}
          </h1>
          <p className="text-white/90 mt-2 text-sm md:text-base max-w-3xl">
            {story.oneLiner}
          </p>
        </div>
      </div>

      {/* Achiever details card - only shown if achiever exists */}
      {achiever && (
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          <div className="h-1.5 w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500"></div>
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-shrink-0">
                <img
                  src={achiever.imageUrl}
                  alt={achiever.author.name}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-amber-200 object-cover"
                />
              </div>
              <div className="flex-grow">
                <h3 className="text-lg md:text-xl font-bold text-gray-900">{achiever.author.name}</h3>
                <p className="text-purple-700 font-medium">{achiever.title}</p>
                <p className="text-gray-600 mt-1 text-sm md:text-base">{achiever.excerpt}</p>
              </div>
              <div className="mt-3 md:mt-0">
                <Button
                  variant="outline"
                  className="border-purple-200 bg-white/80 text-purple-700 hover:bg-purple-50"
                  onClick={() => navigate(`/achievers?id=${achiever.id}`)}
                >
                  <User className="mr-2 h-4 w-4" />
                  View Achiever Profile
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-purple-300 via-purple-500 to-amber-400"></div>

        <div className="p-6 md:p-10">
          {/* Story content with headings and structured content */}
          <div className="prose prose-lg max-w-none mx-auto px-0 md:px-6">
            {/* Introduction */}
            {formattedContent.introduction && (
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                {formattedContent.introduction}
              </p>
            )}

            {/* Sections with headings and bullet points */}
            {formattedContent.sections.map((section, idx) => (
              <div key={idx} className="mb-10">
                <h2 className="text-2xl font-bold text-purple-800 mb-6 border-b border-purple-100 pb-3">
                  {section.heading}
                </h2>

                <ul className="list-disc pl-6 space-y-4">
                  {section.content.map((paragraph, pIdx) => (
                    <li key={pIdx} className="text-gray-700 leading-relaxed">
                      {paragraph}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Quote if available */}
          {story.fullStory.quote && (
            <div className="my-10 border-l-4 border-amber-400 pl-6 py-5 bg-amber-50/50 rounded-r-lg mx-auto max-w-4xl">
              <p className="text-gray-700 italic font-medium text-lg md:text-xl">
                "{story.fullStory.quote}"
              </p>
            </div>
          )}

          {/* Hashtags */}
          {story.fullStory.hashtags && story.fullStory.hashtags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2 mx-auto max-w-4xl">
              {story.fullStory.hashtags.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm text-purple-600 bg-purple-50 px-3 py-1 rounded-full
                          hover:bg-purple-100 hover:text-purple-700 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {story.stats.revenue && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                <h3 className="text-amber-700 font-semibold mb-1">Revenue</h3>
                <p className="text-lg font-bold">{story.stats.revenue}</p>
              </div>
            )}

            {story.stats.impact && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <h3 className="text-purple-700 font-semibold mb-1">Impact</h3>
                <p className="text-lg font-bold">{story.stats.impact}</p>
              </div>
            )}

            {story.stats.employeeCount && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h3 className="text-blue-700 font-semibold mb-1">Team Size</h3>
                <p className="text-lg font-bold">{story.stats.employeeCount}</p>
              </div>
            )}

            {story.stats.partners && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <h3 className="text-green-700 font-semibold mb-1">Partners</h3>
                <p className="text-lg font-bold">{story.stats.partners}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Success Stories */}
      {relatedStories.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Related Success Stories</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedStories.map(relatedStory => (
              <div key={relatedStory.id} onClick={handleRelatedStoryClick}>
                <SuccessStoryBentoCard
                  id={relatedStory.id}
                  name={relatedStory.name}
                  title={relatedStory.title}
                  oneLiner={relatedStory.oneLiner}
                  image={relatedStory.image}
                  stats={relatedStory.stats}
                  size="small"
                  fullStory={relatedStory.fullStory}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessStoryDetail;
